import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { BaseComponent } from 'src/app/pages/base/base.component';
import { Subscription, getEffectiveSubscriptionStatus, SubscriptionStatus } from 'src/app/models/RestModels';
import { ItemInfo, OrderItemInfo, SubscriptionAvailability, SubscriptionInfo } from 'src/app/models/models';
import { GetEmpty } from 'src/app/classes/Empties';

@Component({
	selector: 'app-subscription-manager',
	templateUrl: './subscription-manager.component.html',
	styleUrls: ['./subscription-manager.component.css']
})
export class SubscriptionManagerComponent extends BaseComponent implements OnInit, OnChanges {
	@Input() client_user_id: number | null = null;
	@Input() store_id: number | null = null;
	@Input() order_id: number | null = null;
	@Input() order_items: OrderItemInfo[] = [];
	@Output() changed = new EventEmitter<void>();

	subscription_list: SubscriptionInfo[] = [];
	show_create_form: boolean = false;
	new_subscription: Partial<Subscription> = GetEmpty.subscription();
	availability: SubscriptionAvailability | null = null;
	available_serials: { id:number; serial_number:string; description?:string | null }[] = [];
	asset_item_id: number | null = null;
	leave_in_pool: boolean = false;
	asset_item_list: ItemInfo[] = [];
	subscription_item_list: ItemInfo[] = [];
	subscription_category_id: number | null = null;
	consume_quantities: Record<number, number> = {};
	expanded_id: number | null = null;

	ngOnInit(): void {
		this.loadSubscriptions();
		this.loadAssetItems();
		this.loadSubscriptionItems();
	}

	loadSubscriptionItems(): void {
		this.sink = this.rest.item_info.search({
			eq: { is_subscription: 1, status: 'ACTIVE' } as any,
			limit: 200
		}).subscribe({
			next: (response) => {
				this.subscription_item_list = response.data || [];
			},
			error: () => { this.subscription_item_list = []; }
		});
	}

	get subscriptionCategories(): { id:number | null; name:string }[] {
		const seen = new Map<number | null, string>();
		for (const entry of (this.subscription_item_list || [])) {
			const categoryId = entry.item?.category_id ?? null;
			if (!seen.has(categoryId)) {
				seen.set(categoryId, entry.category?.name || (categoryId == null ? 'Sin categoría' : ('Categoría #' + categoryId)));
			}
		}
		return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
	}

	get filteredSubscriptionItems(): ItemInfo[] {
		if (this.subscription_category_id === null || this.subscription_category_id === undefined) {
			return this.subscription_item_list || [];
		}
		return (this.subscription_item_list || []).filter(entry => (entry.item?.category_id ?? null) == this.subscription_category_id);
	}

	getSubscriptionItemName(id: number | null): string {
		if (!id) {
			return '';
		}
		const found = (this.subscription_item_list || []).find(entry => entry.item?.id == id);
		return found ? found.item.name : ('Artículo #' + id);
	}

	loadAssetItems(): void {
		this.sink = this.rest.item_info.search({
			eq: { has_serial_number: 'YES', status: 'ACTIVE' } as any,
			limit: 100
		}).subscribe({
			next: (response) => {
				this.asset_item_list = response.data || [];
			},
			error: () => { this.asset_item_list = []; }
		});
	}

	getAssetItemName(id: number | null): string {
		if (!id) {
			return '';
		}
		const found = (this.asset_item_list || []).find(entry => entry.item?.id == id);
		return found ? found.item.name : ('Artículo #' + id);
	}

	getSerialLabel(serial: { serial_number:string; description?:string | null }): string {
		return serial.description
			? serial.serial_number + ' — ' + serial.description
			: serial.serial_number;
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['client_user_id'] && !changes['client_user_id'].firstChange) {
			this.loadSubscriptions();
		}
	}

	get subscription_items_in_order(): OrderItemInfo[] {
		return (this.order_items || []).filter(oii => oii.item?.is_subscription == 1 && !oii.order_item?.item_option_id);
	}

	loadSubscriptions(): void {
		this.subscription_list = [];

		if (!this.client_user_id) {
			return;
		}

		this.is_loading = true;
		this.sink = this.rest.subscription_info.search({
			eq: { user_id: this.client_user_id } as any,
			limit: 100,
			sort_order: ['expires_at_ASC']
		}).subscribe({
			next: (response) => {
				this.is_loading = false;
				this.subscription_list = response.data;
			},
			error: (error) => this.showError(error)
		});
	}

	startCreate(order_item_info?: OrderItemInfo): void {
		this.new_subscription = GetEmpty.subscription();
		this.new_subscription.user_id = this.client_user_id || 0;
		this.new_subscription.store_id = this.store_id;
		this.new_subscription.last_order_id = this.order_id;
		this.availability = null;
		this.available_serials = [];
		this.asset_item_id = null;
		this.leave_in_pool = false;

		if (order_item_info) {
			this.new_subscription.item_id = order_item_info.item.id;
			this.new_subscription.is_returnable = 0;
		}

		this.show_create_form = true;
		this.onSubscriptionItemChange();
	}

	onSubscriptionItemChange(): void {
		if (!this.new_subscription.item_id) {
			this.asset_item_id = null;
			this.available_serials = [];
			return;
		}

		this.sink = this.rest.item_info.get(this.new_subscription.item_id).subscribe({
			next: (item_info) => {
				this.asset_item_id = item_info?.item?.asset_item_id || null;
				if (this.asset_item_id && this.new_subscription.store_id && this.new_subscription.expires_at) {
					this.loadAvailableSerials();
				} else {
					this.available_serials = [];
				}
			},
			error: () => { this.asset_item_id = null; }
		});
	}

	checkAvailability(): void {
		if (!this.new_subscription.item_id) {
			this.showError('Seleccione el artículo de suscripción');
			return;
		}

		this.is_loading = true;
		this.sink = this.rest.checkSubscriptionAvailability({
			item_id: this.new_subscription.item_id,
			asset_code: this.new_subscription.asset_code || undefined,
			serial_id: this.new_subscription.serial_id || undefined,
			asset_item_id: this.asset_item_id || undefined,
			starts_at: this.new_subscription.starts_at || undefined,
			expires_at: this.new_subscription.expires_at || undefined,
			store_id: this.new_subscription.store_id
		}).subscribe({
			next: (response) => {
				this.is_loading = false;
				this.availability = response;
				this.available_serials = response.available_serials || [];
			},
			error: (error) => this.showError(error)
		});
	}

	loadAvailableSerials(): void {
		if (!this.asset_item_id || !this.new_subscription.store_id || !this.new_subscription.expires_at) {
			this.showError('Seleccione tipo de activo, sucursal y fecha de fin primero');
			return;
		}

		this.is_loading = true;
		this.sink = this.rest.getAvailableSubscriptionAssets({
			asset_item_id: this.asset_item_id,
			store_id: this.new_subscription.store_id,
			starts_at: this.new_subscription.starts_at || undefined,
			expires_at: this.new_subscription.expires_at
		}).subscribe({
			next: (response) => {
				this.is_loading = false;
				this.available_serials = response || [];
			},
			error: (error) => this.showError(error)
		});
	}

	createSubscription(): void {
		if (!this.client_user_id) {
			this.showError('Seleccione un cliente primero');
			return;
		}

		if (!this.new_subscription.item_id) {
			this.showError('Seleccione el artículo de suscripción');
			return;
		}

		this.new_subscription.user_id = this.client_user_id;
		const payload: any = { ...(this.new_subscription as Subscription) };
		if (this.leave_in_pool) {
			payload.allow_pool = true;
		}
		this.is_loading = true;
		this.sink = this.rest.subscription_info.create(payload).subscribe({
			next: (created: any) => {
				this.is_loading = false;
				this.show_create_form = false;
				const room = created?.subscription?.asset_code || created?.asset_code;
				this.showSuccess(room ? 'Suscripción creada — cuarto ' + room : 'Suscripción creada');
				this.leave_in_pool = false;
				this.loadSubscriptions();
				this.changed.emit();
			},
			error: (error) => this.showError(error)
		});
	}

	consume(subscription_info: SubscriptionInfo): void {
		let qty = this.consume_quantities[subscription_info.subscription.id] || 1;

		this.is_loading = true;
		this.sink = this.rest.consumeSubscription(subscription_info.subscription.id, qty, { store_id: this.store_id || undefined }).subscribe({
			next: () => {
				this.is_loading = false;
				this.showSuccess('Uso registrado');
				this.loadSubscriptions();
				this.changed.emit();
			},
			error: (error) => this.showError(error)
		});
	}

	assign_serial_ids: Record<number, number | null> = {};
	checkin_serials: Record<number, { id:number; serial_number:string; description?:string | null }[]> = {};

	// Estado para mostrar: la vigencia se calcula por periodo, no se lee del status
	// guardado (getEffectiveSubscriptionStatus). El ciclo de vida (suspender,
	// cancelar, devolver) no se toca: eso sigue mandando el status guardado.
	getSubscriptionStatus(subscription_info: SubscriptionInfo): SubscriptionStatus {
		return getEffectiveSubscriptionStatus(subscription_info.subscription);
	}

	needsRoom(subscription_info: SubscriptionInfo): boolean {
		const status = subscription_info.subscription.status;
		if (status !== 'ACTIVE' && status !== 'SUSPENDED') {
			return false;
		}
		if (subscription_info.subscription.serial_id) {
			return false;
		}
		return !!subscription_info.item?.is_asset_tracked || !!subscription_info.item?.asset_item_id;
	}

	loadCheckinSerials(subscription_info: SubscriptionInfo): void {
		const asset_item_id = subscription_info.item?.asset_item_id;
		const store_id = subscription_info.subscription.store_id;
		const starts_at = subscription_info.subscription.starts_at;
		const expires_at = subscription_info.subscription.expires_at;

		if (!asset_item_id || !store_id || !expires_at) {
			this.showError('La suscripción no tiene tipo de activo o vigencia para asignar cuarto');
			return;
		}

		this.is_loading = true;
		this.sink = this.rest.getAvailableSubscriptionAssets({
			asset_item_id: asset_item_id,
			store_id: store_id,
			starts_at: starts_at || undefined,
			expires_at: expires_at,
			exclude_id: subscription_info.subscription.id
		}).subscribe({
			next: (response) => {
				this.is_loading = false;
				this.checkin_serials[subscription_info.subscription.id] = response || [];
			},
			error: (error) => this.showError(error)
		});
	}

	assignSerial(subscription_info: SubscriptionInfo): void {
		const serial_id = this.assign_serial_ids[subscription_info.subscription.id];
		if (!serial_id) {
			this.showError('Seleccione el cuarto a asignar');
			return;
		}

		this.is_loading = true;
		this.sink = this.rest.subscription_info.update({
			id: subscription_info.subscription.id,
			serial_id: serial_id
		} as any).subscribe({
			next: () => {
				this.is_loading = false;
				this.showSuccess('Cuarto asignado');
				this.loadSubscriptions();
				this.changed.emit();
			},
			error: (error) => this.showError(error)
		});
	}

	returnAsset(subscription_info: SubscriptionInfo): void {
		this.is_loading = true;
		this.sink = this.rest.returnSubscriptionAsset(subscription_info.subscription.id).subscribe({
			next: () => {
				this.is_loading = false;
				this.showSuccess('Devolución registrada');
				this.loadSubscriptions();
				this.changed.emit();
			},
			error: (error) => this.showError(error)
		});
	}

	renew(subscription_info: SubscriptionInfo): void {
		this.is_loading = true;
		this.sink = this.rest.renewSubscription(subscription_info.subscription.id).subscribe({
			next: () => {
				this.is_loading = false;
				this.showSuccess('Suscripción renovada');
				this.loadSubscriptions();
				this.changed.emit();
			},
			error: (error) => this.showError(error)
		});
	}

	changeStatus(subscription_info: SubscriptionInfo, status: Subscription['status']): void {
		this.is_loading = true;
		this.sink = this.rest.changeSubscriptionStatus(subscription_info.subscription.id, status).subscribe({
			next: () => {
				this.is_loading = false;
				this.showSuccess('Estado actualizado a ' + status);
				this.loadSubscriptions();
				this.changed.emit();
			},
			error: (error) => this.showError(error)
		});
	}

	toggleExpanded(id: number): void {
		this.expanded_id = this.expanded_id == id ? null : id;
	}

	getItemName(subscription_info: SubscriptionInfo): string {
		return subscription_info.item?.name || ('Artículo #' + subscription_info.subscription.item_id);
	}
}
